import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { useAuthStore } from '../../stores/authStore'
import { selectTourSteps } from './tour-steps'
import {
  useTourStore,
  useTourPreference,
  useSetTourState,
} from './useProductTour'

const MARGIN = 12 // viewport gap for the tooltip
const SPOT_PAD = 6 // spotlight padding around the target

/** The element for a selector, or null when it is absent OR hidden (a zero-box
 *  element — e.g. a nav link inside a collapsed mobile drawer). Hidden targets
 *  are treated as absent so their step is safely skipped. */
function visibleTarget(selector) {
  const el = document.querySelector(selector)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) return null
  return el
}

/**
 * First-login guided product tour. Mounted inside AdminLayout and UserLayout
 * (so it covers Company Admin, Sub Admin and User — never the Super Admin
 * console). Auto-opens the welcome ONLY when the server-persisted state is
 * `not_started`; skip/complete persist so it never auto-starts again. Restart
 * is driven imperatively from `startProductTour()` (UserMenu / User Manual).
 */
export function ProductTour() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)

  // Supported audience only — the Super Admin platform console is excluded.
  const supported = Boolean(user) && user.role !== 'super_admin'

  const phase = useTourStore((s) => s.phase)
  const openWelcome = useTourStore((s) => s.openWelcome)
  const startRunning = useTourStore((s) => s.startRunning)
  const closeStore = useTourStore((s) => s.close)
  const setActiveGroup = useTourStore((s) => s.setActiveGroup)

  const { data: pref } = useTourPreference(supported)
  const setTourState = useSetTourState()

  const steps = useMemo(
    () => (supported ? selectTourSteps({ user, tenant }) : []),
    [supported, user, tenant],
  )

  const [activeSteps, setActiveSteps] = useState([])
  const [stepIndex, setStepIndex] = useState(0)
  const [targetRect, setTargetRect] = useState(null)
  const [tooltipPos, setTooltipPos] = useState({ top: MARGIN, left: MARGIN })
  const tooltipRef = useRef(null)
  const autoOpenedRef = useRef(false)

  /* Still being onboarded — the tour must WAIT.
   *
   * A Google sign-up is seeded `not_started` at account creation
   * (`seedProductTourOnFirstLogin` in the SSO bootstrap), which is before the
   * user has finished anything. Without this gate the welcome auto-opened on
   * the very first dashboard render, landing ON TOP of the Complete Profile
   * modal — the tour's overlay is z-[250] and that modal is z-[200], so the
   * tour won the stack and covered the form the user was required to fill in.
   *
   * Deferring is not just about the overlap. Both onboarding gates deliberately
   * immobilise the shell the tour points at: Complete Profile blocks every
   * sidebar / topbar click behind its overlay, and PlanGate bounces a
   * company_admin with no `planSelectedAt` straight back to /billing. A tour
   * running in either state spotlights navigation the user cannot use, so
   * gating on only the first one would move the bug to /billing rather than fix
   * it.
   *
   * Nothing is lost by waiting: the server records `not_started` and never
   * re-arms OR disarms it, so the effect below simply fires on the first render
   * where onboarding is done. Scoped to company_admin because that is the only
   * role either gate applies to — an invited Sub Admin / member is unaffected
   * and still gets the tour on their first login.
   */
  const onboarding =
    user?.role === 'company_admin' &&
    (user?.profileComplete === false || !tenant?.planSelectedAt)

  // ── Auto-open the welcome the FIRST time only ──
  // The server seeds `state: 'not_started'` on the user's first-ever login and
  // NEVER re-arms it afterwards (see utils/product-tour.js), so this fires once
  // for a new user. Skipping / completing persists a terminal state, so it
  // won't auto-open again until the user restarts it ("Take Product Tour").
  // `autoShow === false` also suppresses it.
  useEffect(() => {
    if (!supported) return
    if (autoOpenedRef.current) return
    if (phase !== 'idle') return
    // Not `autoOpenedRef` — this is a "not yet", not a "never". Leaving the
    // ref unset is what lets the effect re-run and open once onboarding ends.
    if (onboarding) return
    if (pref?.state === 'not_started' && pref?.autoShow !== false) {
      autoOpenedRef.current = true
      openWelcome()
    }
  }, [supported, pref, phase, openWelcome, onboarding])

  // Best-effort persistence of the tour state, held in a ref that is updated in
  // an effect (NOT during render). This keeps the transition handlers below
  // STABLE — an unstable `finish` previously fed a measure→effect update loop.
  const persistRef = useRef(() => {})
  useEffect(() => {
    persistRef.current = (state, lastStepIndex) =>
      setTourState.mutate(
        typeof lastStepIndex === 'number' ? { state, lastStepIndex } : { state },
      )
  })

  const close = useCallback(() => {
    closeStore()
    setTargetRect(null)
  }, [closeStore])

  const finish = useCallback(() => {
    persistRef.current('completed')
    close()
  }, [close])

  const skip = useCallback(() => {
    persistRef.current('skipped')
    close()
  }, [close])

  const start = useCallback(() => {
    persistRef.current('in_progress', 0)
    startRunning()
  }, [startRunning])

  // ── Enter the running tour ──
  // Every entitled step is kept (NOT pruned by current visibility): dropdown
  // groups start CLOSED, so a group-child's target isn't on screen yet — the
  // per-step effect below opens that step's group and then measures.
  useLayoutEffect(() => {
    if (phase !== 'running') return
    if (steps.length === 0) {
      // Nothing to highlight — don't trap the user in an empty overlay.
      setTourState.mutate({ state: 'completed' })
      close()
      return
    }
    setActiveSteps(steps)
    setStepIndex(0)
    setTourState.mutate({ state: 'in_progress', lastStepIndex: 0 })
    // Only re-run when the phase flips to running (welcome → start, or restart).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const step = activeSteps[stepIndex] || null
  const isLast = stepIndex >= activeSteps.length - 1

  const next = useCallback(() => {
    if (isLast) finish()
    else setStepIndex((i) => i + 1)
  }, [isLast, finish])

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1))
  }, [])

  // ── Per step: open ONLY this step's dropdown group, then locate + measure ──
  // Groups start closed and the accordion keeps just one open, so entering a
  // step first opens its group (setActiveGroup) — the sidebar mounts that
  // group's child links on the NEXT render. We therefore wait a frame before
  // measuring, retrying a few frames while the layout settles. If the target
  // never appears (a nav row genuinely hidden for this user), skip forward so
  // the tour can't get stuck on an empty overlay.
  useLayoutEffect(() => {
    if (phase !== 'running') return
    const s = activeSteps[stepIndex]
    if (!s) return
    setActiveGroup(s.group ?? null)
    let raf = 0
    const locate = (tries) => {
      const el = visibleTarget(s.target)
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest' })
        const r = el.getBoundingClientRect()
        setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        return
      }
      if (tries > 0) {
        raf = requestAnimationFrame(() => locate(tries - 1))
        return
      }
      setTargetRect(null)
      if (stepIndex < activeSteps.length - 1) setStepIndex((i) => i + 1)
      else finish()
    }
    raf = requestAnimationFrame(() => locate(4))
    return () => cancelAnimationFrame(raf)
  }, [phase, stepIndex, activeSteps, setActiveGroup, finish])

  // ── Keep the spotlight glued to the target on scroll / resize ──
  // Only updates when the target is present; a transient miss (mid group-open
  // animation) is ignored rather than skipping the step. `setTargetRect` skips
  // an identical rect so a no-op scroll can't churn renders.
  const remeasure = useCallback(() => {
    if (phase !== 'running') return
    const s = activeSteps[stepIndex]
    if (!s) return
    const el = visibleTarget(s.target)
    if (!el) return
    const r = el.getBoundingClientRect()
    const nextRect = { top: r.top, left: r.left, width: r.width, height: r.height }
    setTargetRect((prev) =>
      prev &&
      prev.top === nextRect.top &&
      prev.left === nextRect.left &&
      prev.width === nextRect.width &&
      prev.height === nextRect.height
        ? prev
        : nextRect,
    )
  }, [phase, activeSteps, stepIndex])

  useLayoutEffect(() => {
    if (phase !== 'running') return
    const onChange = () => remeasure()
    window.addEventListener('resize', onChange)
    window.addEventListener('scroll', onChange, true)
    return () => {
      window.removeEventListener('resize', onChange)
      window.removeEventListener('scroll', onChange, true)
    }
  }, [phase, remeasure])

  // ── Position the tooltip near the target, clamped inside the viewport ──
  useLayoutEffect(() => {
    if (phase !== 'running' || !targetRect || !tooltipRef.current) return
    const tip = tooltipRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const placement = step?.placement || 'bottom'
    let top
    let left
    if (placement === 'right' && targetRect.left + targetRect.width + tip.width + MARGIN < vw) {
      left = targetRect.left + targetRect.width + MARGIN
      top = targetRect.top
    } else {
      top = targetRect.top + targetRect.height + MARGIN
      left = targetRect.left
      if (top + tip.height + MARGIN > vh) {
        const above = targetRect.top - tip.height - MARGIN
        if (above > MARGIN) top = above
      }
    }
    left = Math.max(MARGIN, Math.min(left, vw - tip.width - MARGIN))
    top = Math.max(MARGIN, Math.min(top, vh - tip.height - MARGIN))
    setTooltipPos({ top, left })
  }, [phase, targetRect, step, stepIndex])

  // ── Keyboard: Esc = skip, →/Enter = next, ← = previous ──
  useEffect(() => {
    if (phase !== 'running') return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        skip()
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prev()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, skip, next, prev])

  // ── Move focus into the tooltip on each step for screen-reader + keyboard ──
  useEffect(() => {
    if (phase === 'running' && tooltipRef.current) {
      tooltipRef.current.focus({ preventScroll: true })
    }
  }, [phase, stepIndex])

  if (!supported) return null

  // ── Welcome ──
  if (phase === 'welcome') {
    return (
      <Modal
        open
        onClose={skip}
        title="Welcome to TaskMagix"
        description="Let's take a quick tour of the features available to you."
        size="sm"
        overlayZClass="z-[250]"
        footer={
          <>
            <Button variant="secondary" onClick={skip}>
              Skip for Now
            </Button>
            <Button onClick={start} loading={setTourState.isPending}>
              Start Tour
            </Button>
          </>
        }
      >
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          We'll point out the main areas you can use. It only takes a moment, and
          you can leave any time. You can reopen it later from your profile menu
          under <span className="font-medium text-[color:var(--color-ink)]">Take
          Product Tour</span>.
        </p>
      </Modal>
    )
  }

  // ── Running: spotlight + tooltip ──
  if (phase !== 'running' || !step || !targetRect) return null

  const titleId = 'product-tour-title'
  const bodyId = 'product-tour-body'

  return createPortal(
    <div className="fixed inset-0 z-[250]" role="presentation">
      {/* Click-blocker so the dimmed app can't be clicked mid-tour. */}
      <div className="absolute inset-0" aria-hidden="true" />

      {/* Spotlight: a transparent box over the target with a huge shadow that
          dims everything else, plus an accent ring. */}
      <div
        aria-hidden="true"
        className="absolute rounded-lg pointer-events-none transition-all duration-150"
        style={{
          top: targetRect.top - SPOT_PAD,
          left: targetRect.left - SPOT_PAD,
          width: targetRect.width + SPOT_PAD * 2,
          height: targetRect.height + SPOT_PAD * 2,
          boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.55)',
          outline: '2px solid var(--color-accent)',
          outlineOffset: '2px',
        }}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className="absolute w-[320px] max-w-[calc(100vw-24px)] rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 shadow-xl outline-none"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id={titleId}
            className="text-base font-semibold text-[color:var(--color-ink-strong)]"
          >
            {step.title}
          </h2>
          <button
            type="button"
            onClick={skip}
            aria-label="Skip tour"
            className="shrink-0 rounded-md p-1 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-canvas)] hover:text-[color:var(--color-ink)] cursor-pointer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <p id={bodyId} className="mt-1.5 text-sm text-[color:var(--color-ink-muted)]">
          {step.body}
        </p>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span
            className="text-xs font-medium text-[color:var(--color-ink-muted)]"
            aria-live="polite"
          >
            {isLast && activeSteps.length === 1
              ? "You're ready to get started!"
              : `Step ${stepIndex + 1} of ${activeSteps.length}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={stepIndex === 0}
              className="px-2.5 py-1.5 rounded-md text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-canvas)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={next}
              className="px-3 py-1.5 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>

        {!isLast ? (
          <div className="mt-2 text-right">
            <button
              type="button"
              onClick={skip}
              className="text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] underline underline-offset-2 cursor-pointer"
            >
              Skip Tour
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
